import {_libs} from "./../utils/_libs.js";
import {_state} from "./../state/state.js";

/**
 * Single bus page — product gallery: a main Swiper synced to a row of
 * thumbnails, an index badge, and a "Show all photos" fullscreen lightbox.
 * Slides can be images, uploaded <video> files, or YouTube (click-to-play facade).
 *
 * Wired immediately (not via the lazy initSection) so the first arrow/thumb
 * click is handled.
 */
export const busesSingle = () => new Promise((resolve) => {
	const section = document.querySelector('[data-bus-single]');
	if (section && !section.classList.contains('js-bus-single')) {
		section.classList.add('js-bus-single');
		init(section);
	}
	resolve(true);
});

// Lock/unlock page scroll (Lenis on desktop + the theme's overflow-hidden class).
const lockScroll = (lock) => {
	if (_state.lenisInit) lock ? _state.lenisInit.stop() : _state.lenisInit.start();
	document.documentElement.classList.toggle('overflow-hidden', lock);
	document.body.classList.toggle('overflow-hidden', lock);
};

const ytEmbed = (id, autoplay) =>
	`<iframe src="https://www.youtube.com/embed/${id}?rel=0&modestbranding=1${autoplay ? '&autoplay=1' : ''}" title="YouTube video" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>`;

const loadYouTube = (facade, id) => {
	if (!id || facade.classList.contains('is-playing')) return;
	facade.classList.add('is-playing');
	facade.innerHTML = ytEmbed(id, true);
};

const init = (section) => {
	const mainEl = section.querySelector('[data-bus-main]');
	if (!mainEl) return;

	const badge = section.querySelector('[data-bus-index]');
	const thumbs = [...section.querySelectorAll('[data-bus-thumbs] .swiper-slide')];
	const prevEl = section.querySelector('[data-bus-prev]');
	const nextEl = section.querySelector('[data-bus-next]');
	const slides = [...mainEl.querySelectorAll('.swiper-slide')];

	// Structured media list (drives the lightbox).
	const media = slides.map((s) => {
		const type = s.dataset.media || 'image';
		if (type === 'video') {
			const v = s.querySelector('video');
			return {type, src: v?.getAttribute('src') || '', poster: v?.getAttribute('poster') || ''};
		}
		if (type === 'youtube') {
			return {type, yt: s.dataset.yt || '', poster: s.querySelector('img')?.getAttribute('src') || ''};
		}
		return {type: 'image', src: s.querySelector('img')?.getAttribute('src') || ''};
	});

	const setActive = (i) => {
		if (badge) badge.textContent = String(i + 1);
		thumbs.forEach((t, ti) => t.classList.toggle('is-active', ti === i));
	};

	const main = _libs._swiper(mainEl, {
		slidesPerView: 1,
		speed: 500,
		grabCursor: slides.length > 1,
		keyboard: {enabled: true},
		navigation: {prevEl, nextEl},
		on: {
			slideChange() {
				setActive(this.activeIndex);
				// Stop any playing file-video when leaving its slide.
				mainEl.querySelectorAll('video').forEach((v) => v.pause());
			},
		},
	});
	setActive(0);

	thumbs.forEach((t, i) => t.addEventListener('click', () => main.slideTo(i)));

	// YouTube facades → load the (autoplaying) iframe on click.
	section.querySelectorAll('[data-media="youtube"]').forEach((slide) => {
		const facade = slide.querySelector('[data-yt-facade]');
		facade?.addEventListener('click', () => loadYouTube(facade, slide.dataset.yt));
	});

	const allBtn = section.querySelector('[data-bus-all]');
	if (allBtn) {
		allBtn.addEventListener('click', () => openLightbox(media, main.activeIndex));
	}
};

const lightboxSlide = (m) => {
	if (m.type === 'video') {
		return `<div class="swiper-slide"><video class="bus-lightbox__video" src="${m.src}"${m.poster ? ` poster="${m.poster}"` : ''} controls playsinline></video></div>`;
	}
	if (m.type === 'youtube') {
		return `<div class="swiper-slide"><div class="bus-lightbox__yt">${ytEmbed(m.yt, false)}</div></div>`;
	}
	return `<div class="swiper-slide"><img src="${m.src}" alt=""></div>`;
};

const openLightbox = (media, startIndex) => {
	if (!media.length) return;

	const overlay = document.createElement('div');
	overlay.className = 'bus-lightbox';
	overlay.innerHTML = `
		<div class="bus-lightbox__backdrop" data-close></div>
		<button type="button" class="bus-lightbox__close" data-close aria-label="Close"></button>
		<div class="bus-lightbox__swiper swiper">
			<div class="swiper-wrapper">
				${media.map(lightboxSlide).join('')}
			</div>
		</div>
        <button type="button" class="bus-lightbox__nav bus-lightbox__nav--prev"></button>
        <button type="button" class="bus-lightbox__nav bus-lightbox__nav--next"></button>`;
	document.body.appendChild(overlay);
	lockScroll(true);

	const lb = _libs._swiper(overlay.querySelector('.bus-lightbox__swiper'), {
		slidesPerView: 1,
		speed: 400,
		initialSlide: startIndex || 0,
		keyboard: {enabled: true},
		navigation: {
			prevEl: overlay.querySelector('.bus-lightbox__nav--prev'),
			nextEl: overlay.querySelector('.bus-lightbox__nav--next'),
		},
		on: {
			slideChange() {
				overlay.querySelectorAll('video').forEach((v) => v.pause());
			},
		},
	});

	requestAnimationFrame(() => overlay.classList.add('is-open'));

	const close = () => {
		document.removeEventListener('keydown', onKey);
		overlay.classList.remove('is-open');
		lockScroll(false);
		setTimeout(() => {
			if (lb && lb.destroy) lb.destroy(true, true);
			overlay.remove();
		}, 250);
	};
	const onKey = (e) => {
		if (e.key === 'Escape') close();
	};

	overlay.addEventListener('click', (e) => {
		if (e.target.closest('[data-close]')) close();
	});
	document.addEventListener('keydown', onKey);
};
